import React from 'react';

import { CheckResult } from '../types';

interface InstructionsPanelProps {
  failedChecks: CheckResult[];
  manualFallback?: boolean;
}

const instructionsByCheckId: Record<string, string[]> = {
  monitor_count: [
    'Desconecte monitores extras e mantenha apenas 1 monitor.',
    'No Windows: abra Configurações de Tela e escolha "Mostrar apenas em 1".',
    'No macOS: abra Ajustes do Sistema > Monitores e mantenha só um monitor ativo.',
    'Depois, clique em "Corrigir e validar novamente".',
  ],
  webcam_virtual: [
    'No seletor de câmera do navegador, escolha uma webcam física.',
    'Feche apps de câmera virtual, como OBS, ManyCam ou Snap Camera.',
    'Se necessário, reconecte a webcam e valide novamente.',
  ],
  screen_share: [
    'Encerre qualquer compartilhamento de tela em Meet, Zoom, Teams ou similares.',
    'Desative espelhamento ou projeção para telas externas.',
    'Volte para a prova e valide novamente.',
  ],
  virtual_display: [
    'Feche programas de acesso remoto, como AnyDesk, TeamViewer, Parsec ou VNC.',
    'Desconecte sessões de desktop remoto ativas.',
    'Confirme que está usando o monitor físico do computador.',
  ],
  suspicious_extensions: [
    'Desative extensões de tradução, escrita assistida e cola automática.',
    'No Chrome: Menu > Extensões > Gerenciar extensões.',
    'No Firefox: Menu > Extensões e temas.',
    'Depois, valide novamente.',
  ],
};

const manualFallbackSteps = [
  'Confirme que apenas 1 monitor está conectado ao computador.',
  'Confirme que a câmera ativa é física (não virtual).',
  'Feche aplicativos de compartilhamento de tela e acesso remoto.',
  'Desative extensões de navegador não essenciais.',
  'Mantenha o rosto visível, com boa iluminação.',
];

function isSafariMonitorWarning(check: CheckResult) {
  if (check.checkId !== 'monitor_count' || check.status !== 'warning') {
    return false;
  }

  return check.metadata?.browser === 'safari-desktop';
}

export function getInstructionsForCheck(check: CheckResult) {
  if (isSafariMonitorWarning(check)) {
    return [
      'Abra a prova no Google Chrome (o Safari não valida monitores nesta etapa).',
      'No Chrome, retorne para esta tela e execute a validação novamente.',
    ];
  }

  if (check.checkId === 'monitor_count' && check.status === 'warning') {
    return [
      'Confirme que apenas 1 monitor está conectado.',
      'Abra a prova no Google Chrome para concluir a validação automática.',
      'Clique em "Corrigir e validar novamente".',
    ];
  }

  return instructionsByCheckId[check.checkId] ?? [];
}

export function InstructionsPanel({
  failedChecks,
  manualFallback = false,
}: InstructionsPanelProps) {
  if (manualFallback) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h3 className="mb-2 font-semibold text-blue-800">
          Verificação manual necessária
        </h3>
        <p className="mb-3 text-sm text-blue-700">
          Seu navegador não suporta todas as validações automáticas. Confirme
          cada item antes de continuar:
        </p>
        <ol className="space-y-2">
          {manualFallbackSteps.map((step, idx) => (
            <li key={step} className="flex gap-2 text-sm text-blue-800">
              <span className="shrink-0 font-bold">{idx + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (failedChecks.length === 0) return null;

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-800">Como resolver</h3>
      {failedChecks.map(check => {
        const steps = getInstructionsForCheck(check);
        if (steps.length === 0) return null;

        return (
          <div
            key={check.checkId}
            className="rounded-lg border border-orange-200 bg-orange-50 p-4"
          >
            <h4 className="mb-2 font-medium text-orange-800">
              {check.checkName}
            </h4>
            <ol className="space-y-1.5">
              {steps.map((step, idx) => (
                <li key={step} className="flex gap-2 text-sm text-orange-700">
                  <span className="shrink-0 font-bold">{idx + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        );
      })}
    </div>
  );
}
